import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useFetchConsultantClients from 'modules/Consultant/hooks/useFetchConsultantClients';
import useOpenClient from 'modules/Consultant/hooks/useOpenClient';
import useConsultantOverviewStore from 'modules/Consultant/stores/consultantOverviewStore';
import ClientAvatar from './ClientAvatar';
import { resolveRecents } from 'modules/Consultant/utils/clientDirectory';

/**
 * The consultant sidebar's "Recent clients" sub-list under Overview — one click
 * switches straight into a client (same path as the home list: records the
 * consultant org for the back button, lands on Installations). Consultant
 * view only: names come from the live client list, which shares its query
 * cache with the home page. Hidden when collapsed or when nothing was opened.
 */
const RecentClientsNav = ({ isCollapsed }) => {
  const { t } = useTranslation('app');
  const { data: clients = [] } = useFetchConsultantClients();
  const recentClientIds = useConsultantOverviewStore((s) => s.recentClientIds);
  const openClient = useOpenClient();
  const recent = useMemo(
    () => resolveRecents(recentClientIds, clients),
    [recentClientIds, clients],
  );
  if (isCollapsed || recent.length === 0) return null;

  return (
    <div className="ml-4 border-l border-slate-100 pl-2.5">
      <p className="m-0 px-2 pb-0.5 pt-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
        {t('nav.consultant.recentClients', 'Recent clients')}
      </p>
      <ul className="m-0 list-none p-0">
        {recent.map((client) => (
          <li key={client.id}>
            <button
              type="button"
              onClick={() => openClient(client)}
              className="flex w-full items-center gap-2 rounded-md px-2 py-1 text-left text-xs text-slate-600 hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primaryColor/40"
            >
              <ClientAvatar
                name={client.name}
                size="sm"
              />
              <span className="min-w-0 flex-1 truncate">{client.name}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default RecentClientsNav;
